import { useEffect, useRef } from "react";

export function isEmbeddedIframeContext() {
  return typeof window !== "undefined" && window.self !== window.top;
}

export function isLoanAmountField(el) {
  if (!(el instanceof HTMLElement)) return false;
  if (el.getAttribute("name") === "loanAmount") return true;
  const id = el.getAttribute("id");
  return typeof id === "string" && id.endsWith("-loanAmount");
}

export function resetEmbeddedPageScroll() {
  window.scrollTo(0, 0);
  if (document.documentElement) {
    document.documentElement.scrollTop = 0;
  }
  if (document.body) {
    document.body.scrollTop = 0;
  }
  const root = document.getElementById("root");
  if (root) {
    root.scrollTop = 0;
  }
}

/**
 * In partner iframes, block auto-focus/scroll on Amount to finance until the user
 * interacts inside the frame (prevents the 760px embed from jumping on load).
 */
export function useEmbedIframeGuard() {
  const isEmbedded = isEmbeddedIframeContext();
  const interactionReadyRef = useRef(!isEmbedded);

  useEffect(() => {
    if (!isEmbedded) return;

    document.documentElement.setAttribute("data-embed-lock-scroll", "true");

    const sentinel = document.createElement("div");
    sentinel.setAttribute("aria-hidden", "true");
    sentinel.tabIndex = -1;
    Object.assign(sentinel.style, {
      position: "absolute",
      width: "0",
      height: "0",
      overflow: "hidden",
      opacity: "0",
      pointerEvents: "none",
    });
    document.body.prepend(sentinel);

    const focusSentinel = () => {
      if (interactionReadyRef.current) return;
      sentinel.focus({ preventScroll: true });
    };

    focusSentinel();

    const markReady = () => {
      if (interactionReadyRef.current) return;
      interactionReadyRef.current = true;
      document.documentElement.removeAttribute("data-embed-lock-scroll");
      sentinel.remove();
    };

    const blockLoanAmountFocus = (event) => {
      if (interactionReadyRef.current) return;
      if (!isLoanAmountField(event.target)) return;
      if (event.target instanceof HTMLElement) {
        event.target.blur();
      }
      resetEmbeddedPageScroll();
      focusSentinel();
    };

    const enforceScrollTop = () => {
      if (interactionReadyRef.current) return;
      resetEmbeddedPageScroll();
      focusSentinel();
    };

    window.addEventListener("pointerdown", markReady, true);
    window.addEventListener("keydown", markReady, true);
    document.addEventListener("focusin", blockLoanAmountFocus, true);
    window.addEventListener("scroll", enforceScrollTop, { passive: true, capture: true });
    document.addEventListener("scroll", enforceScrollTop, { passive: true, capture: true });

    resetEmbeddedPageScroll();
    const interval = window.setInterval(enforceScrollTop, 100);

    return () => {
      document.documentElement.removeAttribute("data-embed-lock-scroll");
      sentinel.remove();
      window.removeEventListener("pointerdown", markReady, true);
      window.removeEventListener("keydown", markReady, true);
      document.removeEventListener("focusin", blockLoanAmountFocus, true);
      window.removeEventListener("scroll", enforceScrollTop, true);
      document.removeEventListener("scroll", enforceScrollTop, true);
      window.clearInterval(interval);
    };
  }, [isEmbedded]);

  return { isEmbedded };
}
